import React, { useEffect, useState, useCallback } from "react";
import { authStore } from "../lib/api";
import { Layers, MessageSquare, Sparkles, Maximize2, X, ChevronLeft, ChevronRight, Trash2, Send, Eye } from "lucide-react";
import { toast } from "sonner";

const thumb = (fileId) => `${process.env.REACT_APP_BACKEND_URL}/api/files/${fileId}/download?auth=${encodeURIComponent(authStore.get("ali_token") || "")}`;

// CRM-side at-a-glance panel: saved layout plans with homeowner pin-notes overlaid.
export function LayoutPlansSummary({ api, zh, leadId, refreshKey, lead }) {
  const [plans, setPlans] = useState([]);
  const [zoomIdx, setZoomIdx] = useState(null);

  useEffect(() => {
    api.get(`/crm/leads/${leadId}/layout-plans`).then(({ data }) => setPlans(data || [])).catch(() => setPlans([]));
  }, [api, leadId, refreshKey]);

  const nav = useCallback((d) => setZoomIdx((i) => (i == null ? null : (i + d + plans.length) % plans.length)), [plans.length]);

  useEffect(() => {
    if (zoomIdx == null) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") nav(-1);
      else if (e.key === "ArrowRight") nav(1);
      else if (e.key === "Escape") setZoomIdx(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomIdx, nav]);

  if (!plans.length) return null;
  const totalNotes = plans.reduce((s, p) => s + (p.ownerNotes || []).length, 0);
  const zp = zoomIdx != null ? plans[zoomIdx] : null;

  const del = async (p) => {
    if (!window.confirm(zh ? `确定删除方案「${p.name}」？此操作不可撤销。` : `Delete plan "${p.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/crm/leads/${leadId}/layout-plans/${p.id}`);
      setPlans((prev) => prev.filter((x) => x.id !== p.id));
      setZoomIdx(null);
      toast.success(zh ? "方案已删除" : "Plan deleted");
    } catch { toast.error(zh ? "删除失败" : "Delete failed"); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="lead-layout-plans-summary">
      <p className="text-xs font-semibold text-slate-500 mb-2 flex items-center gap-1.5">
        <Layers className="h-3.5 w-3.5 text-red-500" />{zh ? `布局方案 · 业主批注一览 (${plans.length})` : `Layout plans & homeowner notes (${plans.length})`}
        {totalNotes > 0 && <span className="ml-1 text-[10px] bg-amber-500 text-white rounded-full px-1.5 py-0.5 inline-flex items-center gap-0.5" data-testid="lead-plans-note-total"><MessageSquare className="h-2.5 w-2.5" />{totalNotes}</span>}
      </p>
      <div className="flex flex-wrap items-center gap-1.5 mb-2.5" data-testid="lead-plans-owner-status">
        {lead?.proposalShare?.token ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 border border-violet-200 text-violet-700 inline-flex items-center gap-1" data-testid="lead-plans-shared">
            <Send className="h-2.5 w-2.5" />{zh ? "已发业主链接" : "Shared with owner"}
          </span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-400" data-testid="lead-plans-not-shared">{zh ? "未分享业主" : "Not shared"}</span>
        )}
        {(lead?.proposalViews || 0) > 0 ? (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 inline-flex items-center gap-1" data-testid="lead-plans-viewed">
            <Eye className="h-2.5 w-2.5" />{zh ? `业主查看 ${lead.proposalViews} 次` : `Viewed ${lead.proposalViews}×`}
          </span>
        ) : (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-50 border border-slate-200 text-slate-400" data-testid="lead-plans-not-viewed">{zh ? "业主未查看" : "Not viewed"}</span>
        )}
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-700 inline-flex items-center gap-1" data-testid="lead-plans-notes-badge">
          <MessageSquare className="h-2.5 w-2.5" />{zh ? `业主批注 ${totalNotes} 条` : `${totalNotes} owner notes`}
        </span>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {plans.map((p, idx) => {
          const notes = p.ownerNotes || [];
          const aspect = (p.cw || 1) / (p.ch || 1);
          return (
            <div key={p.id} className="rounded-lg border border-slate-200 overflow-hidden relative" data-testid={`lead-plan-${p.id}`}>
              <button data-testid={`lead-plan-del-${p.id}`} onClick={() => del(p)} title={zh ? "删除方案" : "Delete plan"}
                className="absolute top-1 right-1 z-10 bg-white/85 rounded p-1 text-slate-500 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
              <button className="relative block w-full group aspect-[4/3] bg-slate-100 flex items-center justify-center overflow-hidden" onClick={() => setZoomIdx(idx)} data-testid={`lead-plan-zoom-${p.id}`}>
                <div className="relative max-w-full max-h-full" style={{ aspectRatio: `${p.cw || 1} / ${p.ch || 1}`, width: aspect >= (4 / 3) ? "100%" : "auto", height: aspect >= (4 / 3) ? "auto" : "100%" }}>
                  <img src={thumb(p.fileId)} alt={p.name} className="block w-full h-full object-fill" />
                  {notes.map((n, i) => (
                    <span key={n.id || i} data-testid={`lead-plan-pin-${p.id}-${i}`}
                      className="absolute -translate-x-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-amber-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-white shadow"
                      style={{ left: `${(n.x || 0.5) * 100}%`, top: `${(n.y || 0.5) * 100}%` }} title={n.text}>{i + 1}</span>
                  ))}
                </div>
                {p.renderFileId && <span className="absolute top-1 left-1 text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5"><Sparkles className="h-2.5 w-2.5" />{zh ? "效果图" : "AI"}</span>}
                <span className="absolute inset-0 bg-black/0 group-hover:bg-black/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition"><Maximize2 className="h-4 w-4 text-white drop-shadow" /></span>
              </button>
              <div className="px-2 py-1.5">
                <p className="text-[11px] font-medium text-slate-700 truncate">{p.name}{p.aduSf ? ` · ${Number(p.aduSf).toLocaleString()} sf` : ""}</p>
                {notes.length > 0 ? (
                  <ol className="mt-1 list-decimal list-inside space-y-0.5" data-testid={`lead-plan-notes-${p.id}`}>
                    {notes.map((n, i) => <li key={n.id || i} className="text-[10px] text-amber-700 leading-tight">{n.text}</li>)}
                  </ol>
                ) : <p className="text-[10px] text-slate-300">{zh ? "暂无业主批注" : "No homeowner notes"}</p>}
              </div>
            </div>
          );
        })}
      </div>

      {zp && (
        <div className="fixed inset-0 z-[85] bg-black/80 flex items-center justify-center p-4" data-testid="lead-plan-lightbox" onClick={() => setZoomIdx(null)}>
          <button className="mobile-safe-close absolute top-4 right-4 text-white/80 hover:text-white" onClick={(e) => { e.stopPropagation(); setZoomIdx(null); }} data-testid="lead-plan-lightbox-close"><X className="h-7 w-7" /></button>
          <button data-testid="lead-plan-lightbox-del" onClick={(e) => { e.stopPropagation(); del(zp); }} title={zh ? "删除此方案" : "Delete this plan"}
            className="mobile-safe-top-action absolute top-4 left-4 text-white/80 hover:text-red-400 inline-flex items-center gap-1 text-xs"><Trash2 className="h-5 w-5" />{zh ? "删除" : "Delete"}</button>
          {plans.length > 1 && (
            <button data-testid="lead-plan-lightbox-prev" onClick={(e) => { e.stopPropagation(); nav(-1); }} title={zh ? "上一个" : "Previous"}
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-2 transition-colors"><ChevronLeft className="h-6 w-6" /></button>
          )}
          {plans.length > 1 && (
            <button data-testid="lead-plan-lightbox-next" onClick={(e) => { e.stopPropagation(); nav(1); }} title={zh ? "下一个" : "Next"}
              className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/15 hover:bg-white/30 text-white rounded-full p-2 transition-colors"><ChevronRight className="h-6 w-6" /></button>
          )}
          <div className="relative max-w-3xl w-full" onClick={(e) => e.stopPropagation()} data-testid="lead-plan-lightbox-body">
            <img src={thumb(zp.renderFileId || zp.fileId)} alt={zp.name} className="w-full rounded-lg" />
            {!zp.renderFileId && (zp.ownerNotes || []).map((n, i) => (
              <span key={n.id || i} className="absolute -translate-x-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center border-2 border-white"
                style={{ left: `${(n.x || 0.5) * 100}%`, top: `${(n.y || 0.5) * 100}%` }} title={n.text}>{i + 1}</span>
            ))}
            <p className="text-center text-white text-sm mt-2" data-testid="lead-plan-lightbox-caption">{zp.name}{zp.aduSf ? ` · ${Number(zp.aduSf).toLocaleString()} sf` : ""} · {zoomIdx + 1}/{plans.length}</p>
          </div>
        </div>
      )}
    </div>
  );
}
